"use client";

import { sendQuotationEmail } from "@/actions/sendQuotationEmail";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createQuotationFormSchema } from "@/lib/schema";
import { Spinner } from "@/components/icons/icons";
import SectionTitle from "@/components/sections/section-title";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Script from "next/script";
import { Turnstile } from "@marsidev/react-turnstile";
import { useTranslations } from "next-intl";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export default function QuotationForm() {
  const t = useTranslations("QuotationForm");
  const [isPending, startTransition] = useTransition();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);

  const QuotationFormSchema = createQuotationFormSchema(t);
  type FormInputs = z.infer<typeof QuotationFormSchema>;

  const form = useForm<FormInputs>({
    resolver: zodResolver(QuotationFormSchema),
    defaultValues: {
      name: "",
      companyname: "",
      jobtitle: "",
      email: "",
      phone: "",
      website: "",
      services: [], // Changed from service string to services array
      message: "",
    },
  });

  // Define available services
  const serviceOptions = [
    { value: "Web Development", label: t("listService.webDevelopment") },
    { value: "Digital Marketing", label: t("listService.digitalMarketing") },
    { value: "Content Creator", label: t("listService.contentCreation") },
    { value: "Mobile App Development", label: t("listService.mobileApps") },
    { value: "UI/UX Design", label: t("listService.uIUXDesign") },
    { value: "Graphics Designer", label: t("listService.graphicDesign") },
    { value: "SEO", label: t("listService.seo") },
    { value: "Hosting/Cloud Server", label: t("listService.hostingVpsCloud") },
    { value: "WhatsApp Business API", label: t("listService.whasappBusiness") },
    { value: "Mubarokah Qontak CRM", label: t("listService.mubarokahQontakCRM") },
    { value: "custom package from pricing page", label: t("listService.custom") },
  ];

  async function onSubmit(formData: FormInputs) {
    if (!turnstileToken) {
      toast.error(t("error.errorCaptcha"));
      return;
    }
    startTransition(async () => {
      // Verifikasi Turnstile token dengan API
      const turnstileResponse = await fetch("/api/verify-turnstile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ turnstileToken }),
      });

      const turnstileData = await turnstileResponse.json();

      if (!turnstileData.success) {
        toast.error(t("error.errorCaptchaVerification"));
        return;
      }

      // Jika CAPTCHA valid, lanjutkan pengiriman email
      const response = await sendQuotationEmail(formData);

      if (response.success) {
        toast.success(t("success.quotationSent"));
        form.reset();
        setTurnstileToken(null); // Reset Turnstile token
        return;
      }

      if (response.error) {
        toast.error(response.error);
      }
    });
  }

  return (
    <div className="relative z-[1] mx-auto max-w-4xl rounded bg-white px-[12%] py-[8%] dark:bg-slate-900">
      <SectionTitle
        subtitle={t("subtitle")}
        sectionClasses="mx-auto max-w-xl text-center mb-12"
        titleClasses="mb-3 text-center"
        subtitleClasses="text-md font-medium"
      >
        {t("title")}
      </SectionTitle>
      <Form {...form}>
        <form
          className="mt-10 flex flex-col dark:text-black"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-wrap lg:flex-nowrap lg:space-x-8">
            <div className="mb-6 w-full lg:w-1/2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("name")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-6 w-full lg:w-1/2">
              <FormField
                control={form.control}
                name="companyname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">
                      {t("companyName")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-6 w-full lg:w-1/2">
              <FormField
                control={form.control}
                name="jobtitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("jobTitle")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="mb-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-white">{t("email")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-wrap lg:flex-nowrap lg:space-x-8">
            <div className="mb-6 w-full lg:w-1/2">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("phone")}</FormLabel>
                    <FormControl>
                      <div className="flex h-12 w-full rounded-md border bg-muted px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-850 dark:text-white dark:placeholder:text-slate-400">
                        <PhoneInput
                          {...field}
                          international
                          defaultCountry="ID"
                          countrySelectProps={{ className: "custom-select" }}
                          className="bg-transparent focus:outline-none text-sm text-slate-800 dark:text-white"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-6 w-full lg:w-1/2">
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("website")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          {/* SOLUSI BARU: Implementasi multi-select services dengan pendekatan berbeda */}
          <div className="mb-6">
            <FormField
              control={form.control}
              name="services"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-white">{t("service")}</FormLabel>
                  
                  {/* Dropdown trigger button */}
                  <div className="relative">
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full justify-between dark:bg-slate-850 dark:border-slate-800 dark:text-white",
                        !field.value?.length && "text-muted-foreground"
                      )}
                      onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                    >
                      {field.value?.length
                        ? `${field.value.length} ${field.value.length === 1 ? t("serviceSelected") : t("servicesSelected")}`
                        : t("selectValue")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>

                    {/* Simple dropdown with checkboxes */}
                    {servicesDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:text-white">
                        <div className="p-2">
                          <Input 
                            type="text" 
                            placeholder={t("searchServices")} 
                            className="mb-2"
                            onChange={(e) => {
                              // TOOD: Implement search filter
                            }}
                          />
                        </div>
                        <div className="max-h-60 overflow-y-auto p-2">
                          {serviceOptions.map((option) => (
                            <div 
                              key={option.value} 
                              className="flex items-center space-x-2 px-2 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer rounded-sm"
                              onClick={() => {
                                // Toggle service selection
                                const selected = field.value || [];
                                const isSelected = selected.includes(option.value);
                                
                                let newValue;
                                if (isSelected) {
                                  newValue = selected.filter(v => v !== option.value);
                                } else {
                                  newValue = [...selected, option.value];
                                }
                                
                                // Update form
                                field.onChange(newValue);
                              }}
                            >
                              <div 
                                className={cn(
                                  "flex h-4 w-4 items-center justify-center rounded-sm border border-slate-300",
                                  field.value?.includes(option.value) 
                                    ? "bg-slate-900 border-slate-900 dark:bg-slate-200 dark:border-slate-200" 
                                    : "bg-white dark:bg-transparent"
                                )}
                              >
                                {field.value?.includes(option.value) && (
                                  <Check className="h-3 w-3 text-white dark:text-slate-900" />
                                )}
                              </div>
                              <span className="text-sm">{option.label}</span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t p-2 flex justify-end">
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => setServicesDropdownOpen(false)}
                          >
                            {t("done")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Show selected services as badges */}
                  {field.value?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {field.value.map(value => {
                        const serviceOption = serviceOptions.find(opt => opt.value === value);
                        return (
                          <Badge key={value} variant="secondary" className="text-xs py-1">
                            {serviceOption?.label}
                            <button
                              type="button"
                              className="ml-1 rounded-full outline-none"
                              onClick={(e) => {
                                e.preventDefault();
                                const newValue = field.value.filter(v => v !== value);
                                field.onChange(newValue);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="mb-6">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-white">{t("message")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("messagePlaceholder")}
                      rows={6}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="mb-6">
            <Turnstile
              siteKey="0x4AAAAAAA29-0PYk7u7JLd5"
              onSuccess={(token) => setTurnstileToken(token)}
            />
          </div>
          <Button type="submit" size="lg" disabled={isPending}>
            {isPending ? (
              <>
                <Spinner className="mr-2 h-5 w-5 animate-spin" />
                <span>{t("sending")}</span>
              </>
            ) : (
              <span>{t("sendQuotation")}</span>
            )}
          </Button>
        </form>
      </Form>
      <div className="mt-6 text-center">
        <p className="mb-4">{t("or")}</p>
        <Script
          src="https://meetings.brevo.com/assets/libs/popup.min.js"
          strategy="lazyOnload"
        />
        <link
          href="https://meetings.brevo.com/assets/styles/popup.css"
          rel="stylesheet"
        />
        <Button
          size="lg"
          className="bg-black text-white border-0 shadow-md shadow-black/40 hover:shadow-black/60 rounded-lg"
          onClick={() => {
            if (typeof window !== "undefined" && window.BrevoBookingPage) {
              window.BrevoBookingPage.initStaticButton({
                url: 'https://meet.brevo.com/mubarokah-digital-nusantara/borderless?l=get-a-quotation'
              });
            }
            // Tracking the button click with LinkedIn
            window.lintrk('track', { conversion_id: 17502220 });
            return false;
          }}
        >
          {t("bookConsultation")}
        </Button>
      </div>
    </div>
  );
}